import { deviceDescription } from '@api/devices/identity-contracts';
import type { CustomerMigration, MigrationLinks, MigrationPreview, MigrationRun, MigrationStep, NinjaRetirement } from '@api/migration/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, ChevronDown, KeyRound, ShieldAlert } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useId, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { approveMigrationStep, cancelMigration, getCustomerMigration, isStatus, migrationDeviceAction, migrationStepOptions, previewMigration,
  requestMigrationFalcon, setMigrationLinks, setSiteMigrationLinks, startMigration } from '../lib/api';
import { describeKeyError, signWithKey } from '../lib/webauthn';
import { BatchApprovalDialog } from '../operations/batch';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { ErrorPanel } from '../ui/errors';
import { Card, Skeleton } from '../ui/misc';
import { useToast } from '../ui/toast';
import { cn } from '../ui/cn';

// A customer's move from SentinelOne to CrowdStrike Falcon, and later off NinjaOne by hand (WRMM-185,
// docs/planning/22-customer-migration.md). Workspace owners only: vendor links, a preview of the customer's enrolled
// computers, the run's computers with one key touch per step, and each finished computer's NinjaOne readiness.

const STEP: Record<MigrationStep, string> = { check: 'Checking', sentinelone: 'Remove SentinelOne', falcon: 'Install Falcon', finished: 'Finished' };
const STATE_TONE: Record<string, string> = { failed: 'text-attention', unknown: 'text-attention', skipped: 'text-ink-3', done: 'text-healthy' };
const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';
const key = (mspId: string, organizationId: string) => ['scope', mspId, 'migration', organizationId];
const failure = (error: unknown) => isStatus(error, 409) ? (error as Error).message.startsWith('Request failed') ? 'Not possible right now; refresh and look again.' : (error as Error).message
  : isStatus(error, 403) ? 'Only a workspace owner can do this.' : isStatus(error, 400) ? 'Check the values.' : 'Try again.';
const plural = (n: number) => `${n} computer${n === 1 ? '' : 's'}`;

/** Collapsed until opened, and loaded only then: most visits to a customer never need it. */
export function CustomerMigrationCard({ organizationId, name }: { organizationId: string; name?: string }) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const panel = useId();
  return (
    <Card>
      <h2>
        <button type="button" aria-expanded={open} aria-controls={panel} onClick={() => { setOpen(!open); setOpened(true); }}
          className={cn('flex w-full items-center gap-2 px-5 py-4 text-left hover:bg-hover', open ? 'rounded-t-xl' : 'rounded-xl')}>
          <ChevronDown className={cn('size-4 shrink-0 text-ink-3 transition-transform', !open && '-rotate-90')} />
          <span className="block text-[15px] font-semibold">Move from SentinelOne to Falcon</span>
        </button>
      </h2>
      {opened && <div id={panel} hidden={!open} className="border-t border-line p-5"><CustomerMigrationSection organizationId={organizationId} name={name} /></div>}
    </Card>
  );
}

export function CustomerMigrationSection({ organizationId, name }: { organizationId: string; name?: string }) {
  const { mspId } = useScope();
  const migration = useQuery({ queryKey: key(mspId, organizationId), queryFn: ({ signal }) => getCustomerMigration(mspId, organizationId, signal),
    // While a run is active, and while NinjaOne readiness lines are shown: "Ready" is only as good as its last check.
    refetchInterval: q => (q.state.data?.run?.status === 'active' || q.state.data?.ninja.computers.length ? 15_000 : false) });
  if (migration.isPending) return <Skeleton className="h-24 w-full" />;
  if (migration.isError) return <ErrorPanel error={migration.error} onRetry={() => void migration.refetch()} />;
  const { connected, links, run, ninja } = migration.data;
  return (
    <div className="space-y-5">
      <p className="text-[12.5px] text-ink-3">
        Removes SentinelOne from {name ?? 'this customer'}’s computers and installs CrowdStrike Falcon right after, one customer at a time. Install
        wittco-rmm on them from NinjaOne first; NinjaOne stays until you remove it by hand. Each step needs your key.
        {connected ? '' : ' The vendor connections aren’t set up on this server yet: links can be saved, but nothing can start.'}
      </p>
      {run?.status === 'active' ? <RunView organizationId={organizationId} run={run} /> : <>
        <LinksEditor organizationId={organizationId} links={links} />
        {connected && <Start organizationId={organizationId} />}
        {run && <RunView organizationId={organizationId} run={run} />}
      </>}
      <NinjaOneRetirement key={organizationId} ninja={ninja} />
    </div>
  );
}

type CustomerEdit = { s1: string; ccid: string; tier: string; ninjaDone: boolean };
type SiteEdit = { group: string; tag: string; policy: string; update: string };
const emptySite: SiteEdit = { group: '', tag: '', policy: '', update: '' };
const customerValues = (links: MigrationLinks): CustomerEdit => ({ s1: links.s1SiteId ?? '', ccid: links.falconCcid ?? '',
  tier: links.falconTier ?? '', ninjaDone: !!links.ninjaRetirementConfirmedAt });
const siteValues = (links: MigrationLinks): Record<string, SiteEdit> => Object.fromEntries(links.sites.map(s => [s.siteId,
  { group: s.falconHostGroupId ?? '', tag: s.falconGroupingTag ?? '', policy: s.falconPreventionPolicyId ?? '', update: s.falconUpdatePolicyId ?? '' }]));
const unchanged = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function LinksEditor({ organizationId, links }: { organizationId: string; links: MigrationLinks }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const [customer, setCustomer] = useState<CustomerEdit>(() => customerValues(links));
  const [savedCustomer, setSavedCustomer] = useState<CustomerEdit>(() => customerValues(links));
  const [sites, setSites] = useState<Record<string, SiteEdit>>(() => siteValues(links));
  const [savedSites, setSavedSites] = useState<Record<string, SiteEdit>>(() => siteValues(links));
  const justSaved = useRef<Set<string>>(new Set());
  useEffect(() => {
    const nextCustomer = customerValues(links);
    const nextSites = siteValues(links);
    setCustomer(current => justSaved.current.has('customer') || unchanged(current, savedCustomer) ? nextCustomer : current);
    setSites(current => Object.fromEntries(links.sites.map(s => {
      const edit = current[s.siteId] ?? emptySite;
      return [s.siteId, justSaved.current.has(`site:${s.siteId}`) || unchanged(edit, savedSites[s.siteId] ?? emptySite) ? nextSites[s.siteId]! : edit];
    })));
    setSavedCustomer(nextCustomer);
    setSavedSites(nextSites);
    justSaved.current.clear();
  }, [links]);
  const customerDirty = !unchanged(customer, savedCustomer);
  const anythingDirty = customerDirty || links.sites.some(s => !unchanged(sites[s.siteId] ?? emptySite, savedSites[s.siteId] ?? emptySite));
  const blocker = useBlocker(({ currentLocation, nextLocation }) => anythingDirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!anythingDirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anythingDirty]);
  const refresh = () => client.invalidateQueries({ queryKey: key(mspId, organizationId) });
  const writeCustomer = (edit: CustomerEdit) => setMigrationLinks(mspId, organizationId, { s1SiteId: edit.s1.trim() || null,
    falconCcid: edit.ccid.trim() || null, falconTier: (edit.tier || null) as 'complete' | 'advanced_defend' | null,
    ninjaRetirementConfirmed: edit.ninjaDone });
  const customerSaved = (edit: CustomerEdit) => { justSaved.current.add('customer'); setSavedCustomer(edit); };
  const saveCustomer = useMutation({
    mutationFn: (edit: CustomerEdit) => writeCustomer(edit),
    onSuccess: (_, edit) => { customerSaved(edit); void refresh(); toast({ title: 'Customer links saved' }); },
    onError: error => toast({ tone: 'warning', title: 'Not saved', body: failure(error) }),
  });
  const id = (v: string) => v.trim().toLowerCase() || null;
  const saveSite = useMutation({
    mutationFn: async ({ siteId, site, customer: pendingCustomer }: { siteId: string; site: SiteEdit; customer: CustomerEdit | null }) => {
      if (pendingCustomer) { await writeCustomer(pendingCustomer); customerSaved(pendingCustomer); }
      return setSiteMigrationLinks(mspId, siteId, { falconHostGroupId: id(site.group), falconGroupingTag: site.tag.trim() || null,
        falconPreventionPolicyId: id(site.policy), falconUpdatePolicyId: id(site.update) });
    },
    onSuccess: (_, { siteId, site }) => { justSaved.current.add(`site:${siteId}`); setSavedSites(current => ({ ...current, [siteId]: site }));
      void refresh(); toast({ title: 'Site links saved' }); },
    onError: error => toast({ tone: 'warning', title: 'Not saved', body: failure(error) }),
  });
  const saving = saveCustomer.isPending || saveSite.isPending;
  return (
    <div className="space-y-4">
      <h3 className="text-[13px] font-medium text-ink-2">Vendor links</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-[13px]"><span className="text-ink-2">SentinelOne site ID</span>
          <input className={field} inputMode="numeric" value={customer.s1} onChange={e => setCustomer({ ...customer, s1: e.target.value })} /></label>
        <label className="text-[13px]"><span className="text-ink-2">Falcon CID with checksum (child CID)</span>
          <input className={cn(field, 'font-mono')} placeholder="0123…cdef-AB" value={customer.ccid} onChange={e => setCustomer({ ...customer, ccid: e.target.value })} /></label>
        <label className="text-[13px]"><span className="text-ink-2">Falcon tier</span>
          <select className={field} value={customer.tier} onChange={e => setCustomer({ ...customer, tier: e.target.value })}>
            <option value="">Not chosen</option><option value="complete">Falcon Complete</option><option value="advanced_defend">Advanced Defend</option>
          </select></label>
      </div>
      <label className="flex items-start gap-2 text-[13px]">
        <input type="checkbox" className="mt-1" checked={customer.ninjaDone} onChange={e => setCustomer({ ...customer, ninjaDone: e.target.checked })} />
        <span>Ready to leave NinjaOne
          <span className="block text-[12.5px] text-ink-3">The customer’s people who remote in have wittco-rmm accounts, and its patching is covered. Until then, no computer shows as ready to remove NinjaOne.</span></span>
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={saving} onClick={() => saveCustomer.mutate(customer)}>Save customer links</Button>
        {customerDirty && <span className="text-[12.5px] text-attention">Unsaved changes</span>}
      </div>
      <div className="space-y-3">
        {links.sites.map(s => { const v = sites[s.siteId] ?? emptySite; const set = (patch: Partial<typeof v>) => setSites({ ...sites, [s.siteId]: { ...v, ...patch } });
          const dirty = !unchanged(v, savedSites[s.siteId] ?? emptySite);
          return (
            <div key={s.siteId} className="grid items-end gap-2 rounded-lg border border-line p-3 sm:grid-cols-[1fr_1.6fr_1.2fr_1.6fr_1.6fr_auto]">
              <div className="text-[13.5px] font-medium sm:self-center">{s.siteName}</div>
              <label className="text-[12.5px]"><span className="text-ink-3">Falcon host group ID</span>
                <input className={cn(field, 'font-mono')} value={v.group} onChange={e => set({ group: e.target.value })} /></label>
              <label className="text-[12.5px]"><span className="text-ink-3">Sensor grouping tag</span>
                <input className={cn(field, 'font-mono')} placeholder="wittco/main" value={v.tag} onChange={e => set({ tag: e.target.value })} /></label>
              <label className="text-[12.5px]"><span className="text-ink-3">Prevention policy ID</span>
                <input className={cn(field, 'font-mono')} value={v.policy} onChange={e => set({ policy: e.target.value })} /></label>
              <label className="text-[12.5px]"><span className="text-ink-3">Sensor update policy ID</span>
                <input className={cn(field, 'font-mono')} value={v.update} onChange={e => set({ update: e.target.value })} /></label>
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={saving} onClick={() => saveSite.mutate({ siteId: s.siteId, site: v, customer: customerDirty ? customer : null })}>Save</Button>
                {dirty && <span className="text-[12.5px] text-attention">Unsaved changes</span>}
              </div>
            </div>
          ); })}
      </div>
      <Dialog.Root open={blocker.state === 'blocked'} onOpenChange={open => { if (!open) blocker.reset?.(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(520px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
            <Dialog.Title className="text-[17px] font-semibold">Unsaved changes</Dialog.Title>
            <Dialog.Description className="mt-1 text-[13px] text-ink-2">Leaving this page will discard the migration links you haven’t saved.</Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => blocker.reset?.()}>Keep editing</Button>
              <Button variant="primary" onClick={() => blocker.proceed?.()}>Discard changes</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function Start({ organizationId }: { organizationId: string }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const [plan, setPlan] = useState<MigrationPreview | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [sensor, setSensor] = useState('');
  const look = useMutation({ mutationFn: () => previewMigration(mspId, organizationId),
    // A customer's first run is one computer (the canary): nothing is preselected then; otherwise every one it can take.
    onSuccess: next => { setPlan(next); setSensor(next.sensors[0]?.sha256 ?? ''); setChosen(new Set(next.firstRun ? [] : next.devices.filter(d => d.included).map(d => d.deviceId))); },
    onError: error => toast({ tone: 'warning', title: 'No preview', body: failure(error) }) });
  const start = useMutation({
    mutationFn: () => startMigration(mspId, organizationId, [...chosen], sensor),
    onSuccess: () => { setPlan(null); void client.invalidateQueries({ queryKey: key(mspId, organizationId) }); toast({ title: 'Migration started' }); },
    onError: error => toast({ tone: 'warning', title: 'Not started', body: failure(error) }),
  });
  const toggle = (id: string) => setChosen(old => { const next = new Set(old); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const tooMany = !!plan?.firstRun && chosen.size > 1;
  return (
    <div className="space-y-3">
      <h3 className="text-[13px] font-medium text-ink-2">Start a migration</h3>
      <Button size="sm" disabled={look.isPending} onClick={() => look.mutate()}>{plan ? 'Look again' : 'Preview the customer’s computers'}</Button>
      {plan && <>
        <ul className="divide-y divide-line rounded-lg border border-line text-[13px]">
          {plan.devices.map(d => (
            <li key={d.deviceId} className="flex flex-wrap items-center gap-x-3 px-3 py-2">
              <input type="checkbox" aria-label={`Include ${deviceDescription(d)}`} disabled={!d.included} checked={chosen.has(d.deviceId)} onChange={() => toggle(d.deviceId)} />
              <span className={cn('font-medium', !d.included && 'text-ink-3')}>{deviceDescription(d)}</span>
              <span className="text-ink-3">{d.siteName ?? 'no site'}</span>
              {d.reason && <span className="text-ink-3">— left out: {d.reason}</span>}
            </li>
          ))}
          {!plan.devices.length && <li className="px-3 py-2 text-ink-3">wittco-rmm has no computers for this customer yet: install it on them from NinjaOne first.</li>}
        </ul>
        <label className="block text-[13px]"><span className="text-ink-2">Falcon sensor to install</span>
          <select className={field} value={sensor} onChange={e => setSensor(e.target.value)}>
            {plan.sensors.map(s => <option key={s.sha256} value={s.sha256}>{s.version}{s.releasedAt ? ` (${s.releasedAt.slice(0, 10)})` : ''}</option>)}
            {!plan.sensors.length && <option value="">None offered</option>}
          </select></label>
        {plan.firstRun && <p className="text-[12.5px] text-ink-3">This customer’s first migration takes one computer. Start the rest once it has finished.</p>}
        <Button variant="primary" disabled={!chosen.size || !sensor || tooMany || start.isPending} onClick={() => start.mutate()}>Start with {plural(chosen.size)}</Button>
      </>}
    </div>
  );
}

function RunView({ organizationId, run }: { organizationId: string; run: MigrationRun }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const [batchId, setBatchId] = useState<string | null>(null);
  const update = (next: MigrationRun) => client.setQueryData<CustomerMigration>(key(mspId, organizationId), old => (old ? { ...old, run: next } : old));
  const approve = useMutation({
    mutationFn: async (deviceIds: string[]) =>
      approveMigrationStep(mspId, run.id, 'sentinelone', deviceIds, await signWithKey(await migrationStepOptions(mspId, run.id, 'sentinelone', deviceIds))),
    onSuccess: next => { update(next); toast({ title: 'Approved', body: 'SentinelOne is removed from each computer once its Falcon job has reached it.' }); },
    onError: error => toast({ tone: 'warning', title: 'Not approved', body: error instanceof DOMException ? describeKeyError(error) : failure(error) }),
  });
  // Falcon's two batches, each approved in turn: staging the installer, then the install that waits for SentinelOne.
  const falcon = useMutation({ mutationFn: ({ siteId, part }: { siteId: string; part: 'falcon' | 'falcon-install' }) => requestMigrationFalcon(mspId, run.id, siteId, part),
    onSuccess: ({ run: next, batch }) => { update(next); setBatchId(batch?.id ?? null); },
    onError: error => toast({ tone: 'warning', title: 'Falcon not requested', body: failure(error) }) });
  const act = useMutation({ mutationFn: ({ id, action }: { id: string; action: 'retry' | 'skip' }) => migrationDeviceAction(mspId, run.id, id, action), onSuccess: update,
    onError: error => toast({ tone: 'warning', title: 'Not done', body: failure(error) }) });
  const cancel = useMutation({ mutationFn: () => cancelMigration(mspId, run.id), onSuccess: update,
    onError: error => toast({ tone: 'warning', title: 'Not canceled', body: failure(error) }) });
  const active = run.status === 'active';
  // Falcon first (staging, then install), then SentinelOne: S1 goes once Falcon is staged and waiting on the computer.
  const order = { falcon: 0, 'falcon-install': 1, sentinelone: 2 } as const;
  const ready = [...run.ready].sort((a, b) => order[a.step] - order[b.step]);
  const devices = [...run.devices].sort((a, b) => Number(b.withoutEdr) - Number(a.withoutEdr));
  const exposed = run.devices.filter(d => d.withoutEdr).length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex-1 text-[13px] font-medium text-ink-2"><ArrowRightLeft className="mr-1.5 inline size-4 text-ink-3" />
          {active ? 'Migration running' : run.status === 'finished' ? 'Last migration finished' : 'Last migration canceled'}</h3>
        {active && <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => { if (window.confirm('Cancel this migration? Nothing more is sent; computers keep what they have, and a Falcon install already replacing SentinelOne goes on.')) cancel.mutate(); }}>Cancel migration</Button>}
      </div>
      {exposed > 0 && (
        <p className="flex items-center gap-2 rounded-lg border border-attention/40 bg-attention/5 px-3 py-2 text-[13.5px] text-attention">
          <ShieldAlert className="size-4 shrink-0" />{plural(exposed)} without SentinelOne and not yet protected by Falcon.
        </p>
      )}
      {active && !run.startedByMe && run.ready.length > 0 && (
        <p className="text-[12.5px] text-ink-3">Only the person who started this migration approves its steps.</p>
      )}
      {active && run.startedByMe && ready.map(r => (
        <div key={`${r.step}:${r.siteId ?? ''}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2 text-[13.5px]">
          <span className="flex-1">{r.step === 'falcon' ? `Stage Falcon’s installer on ${plural(r.deviceIds.length)} at ${r.siteName}`
            : r.step === 'falcon-install' ? `Install Falcon on ${plural(r.deviceIds.length)} at ${r.siteName} (it waits for SentinelOne to go)`
            : `Remove SentinelOne from ${plural(r.deviceIds.length)}`}</span>
          <Button size="sm" variant="primary" disabled={approve.isPending || falcon.isPending}
            onClick={() => (r.step === 'sentinelone' ? approve.mutate(r.deviceIds) : falcon.mutate({ siteId: r.siteId!, part: r.step }))}>
            <KeyRound className="size-3.5" />Approve with your key</Button>
        </div>
      ))}
      {!!falcon.data?.assessments?.length && <ul aria-label="Package compatibility results" className="space-y-1 text-[13px]">
        {falcon.data.assessments.filter(a => a.status !== 'eligible').map(a => <li key={a.deviceId}>
          {run.devices.find(d => d.deviceId === a.deviceId)?.hostname ?? a.deviceId}: {a.reason}
        </li>)}
      </ul>}
      <ul className="divide-y divide-line rounded-lg border border-line text-[13px]">
        {devices.map(d => (
          <li key={d.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2', d.withoutEdr && 'bg-attention/5')}>
            <span className="min-w-32 font-medium">{deviceDescription(d)}</span>
            <span className="text-ink-3">{d.siteName}</span>
            <span>{STEP[d.step]}</span>
            <span className={cn('text-ink-2', STATE_TONE[d.state])}>{d.state}</span>
            {d.withoutEdr && <span className="text-attention">no EDR</span>}
            {d.detail && <span className="text-ink-3">{d.detail}</span>}
            {active && d.state === 'failed' && <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: d.id, action: 'retry' })}>Retry</Button>}
            {active && !d.withoutEdr && ['waiting', 'failed'].includes(d.state) && <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: d.id, action: 'skip' })}>Skip</Button>}
          </li>
        ))}
      </ul>
      <BatchApprovalDialog batchId={batchId} open={!!batchId} onOpenChange={open => { if (!open) { setBatchId(null); void client.invalidateQueries({ queryKey: key(mspId, organizationId) }); } }}
        onApproveOne={() => undefined} />
    </div>
  );
}

/** Expanded existing retirement checks; the full CSV comparison stays in the private-local tool. */
function NinjaOneRetirement({ ninja }: { ninja: NinjaRetirement }) {
  return (
    <div className="space-y-2">
      <h3 className="text-[13px] font-medium text-ink-2">Removing NinjaOne</h3>
      <p className="text-[12.5px] text-ink-3">
        Run the private-local NinjaOne CSV comparison before each removal batch. Every computer must pass, or Harry must accept each named exception.
        Serial and hostname matches are device-reported hints: CSV identity remains unverified and needs Harry’s named exception. Passing checks here cannot verify the matching NinjaOne computer. These per-computer checks do not establish that the export is complete. Removal remains a separate action in NinjaOne.
      </p>
      {ninja.inventory && <p className="text-[12.5px] text-ink-3">Site IDs for the local comparison mapping: {ninja.inventory.sites.map(s => `${s.name}: ${s.id}`).join('; ')}.</p>}
      <p className="text-[12.5px] text-ink-3">Falcon protection is the install’s recorded confirmation against current customer/site links, not a new provider verification.
        The card can show that record with older inventory; the local CSV comparison requires inventory from the last 15 minutes.
        A completed older migration can supply the report’s Falcon proof when current approved settings still match, even if no newer CrowdStrike choice is recorded.</p>
      <ul className="divide-y divide-line rounded-lg border border-line text-[13px]">
        {ninja.computers.map(c => (
          <li key={c.deviceId} className="flex flex-wrap items-center gap-x-3 px-3 py-2">
            <span className="min-w-32 font-medium">{deviceDescription(c)}</span>
            <span className={c.ready ? 'text-healthy' : 'text-ink-3'}>{c.detail}</span>
            {ninja.inventory?.computers.find(d => d.deviceId === c.deviceId)?.protection.confirmedAt && <span className="text-ink-3 text-[12px]">
              Falcon recorded confirmation: {ninja.inventory.computers.find(d => d.deviceId === c.deviceId)!.protection.confirmedAt}
            </span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
